import React, {ReactElement, useEffect, useState} from 'react';
import {ThemeProvider} from 'styled-components';
import {ViziblyTheme} from '../../../styles/zendesk-garden/ViziblyZDGTheme';
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from '@zendeskgarden/react-tables';
import {Field, Label, Toggle} from '@zendeskgarden/react-forms';
import {Col, Grid, Row as GridRow} from '@zendeskgarden/react-grid';
import {useListTemplatePropertiesLazyQuery} from "../../../__generated__/generated_types";
import {Dots} from '@zendeskgarden/react-loaders';
import {Button} from '@zendeskgarden/react-buttons';
import {TemplatePropertyCreateModal} from './TemplatePropertyCreateModal';
import {TemplatePropertyEditModal} from './TemplatePropertyEditModal';
import {AdminWrapper} from "../../admin/AdminWrapper";

import * as css from "./styles/templatePropertyStyles.module.scss";

interface TemplateProperty {
    id: string;
    name: string;
    reforecastYear: number;
    reforecastStartMonthIndex: number;
    isDeactivated: boolean;
}

interface TemplatePropertyListProps {
    budgetYear: number;
}

export function TemplatePropertyList(props: TemplatePropertyListProps): ReactElement {
    const [includeDeactivated, setIncludeDeactivated] = useState<boolean>(false);
    const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
    const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
    const [selectedTemplatePropertyId, setSelectedTemplatePropertyId] = useState<string | undefined>(undefined);

    const [fetchTemplateProperties, {data, loading}] = useListTemplatePropertiesLazyQuery({
        fetchPolicy: 'no-cache',
    });

    const loadData = () => {
        fetchTemplateProperties({
            variables: {
                includeDeactivated,
                budgetYear: props.budgetYear
            }
        });
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadData();
    }, [includeDeactivated]);

    const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIncludeDeactivated(event.target.checked);
    };

    const openCreateModal = () => {
        setCreateModalVisible(true);
    };

    const closeCreateModal = () => {
        setCreateModalVisible(false);
    };

    const openEditModal = (id: string) => {
        setSelectedTemplatePropertyId(id);
        setEditModalVisible(true);
    };

    const closeEditModal = () => {
        setSelectedTemplatePropertyId(undefined);
        setEditModalVisible(false);
    };

    const refreshData = () => {
        loadData();
    };

    const getMonthName = (monthIndex: number): string => {
        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return months[monthIndex] || `Month ${monthIndex}`;
    };

    const templateProperties: TemplateProperty[] = data?.listTemplateProperties || [];

    return (
            <ThemeProvider theme={ViziblyTheme}>
                <AdminWrapper>
                    <Grid>
                        <GridRow>
                            <Col>
                                <div className={css.headerActions}>
                                    <h2>Template Properties</h2>
                                    <Button isPrimary onClick={openCreateModal}>Create Template Property</Button>
                                </div>
                            </Col>
                        </GridRow>
                        <GridRow>
                            <Col>
                                <Field>
                                    <Toggle onChange={handleToggleChange} checked={includeDeactivated}>
                                        <Label>Include Deactivated</Label>
                                    </Toggle>
                                </Field>
                            </Col>
                        </GridRow>
                        <GridRow>
                            <Col>
                                {loading ? (
                                        <Dots size={32}/>
                                ) : (
                                        <Table>
                                            <Head>
                                                <HeaderRow>
                                                    <HeaderCell>Name</HeaderCell>
                                                    <HeaderCell>Reforecast Year</HeaderCell>
                                                    <HeaderCell>Reforecast Start Month</HeaderCell>
                                                    <HeaderCell>Status</HeaderCell>
                                                </HeaderRow>
                                            </Head>
                                            <Body>
                                                {templateProperties.length === 0 ? (
                                                        <Row>
                                                            <Cell colSpan={4}>No template properties found</Cell>
                                                        </Row>
                                                ) : (
                                                        templateProperties.map((property) => (
                                                                <Row
                                                                    key={property.id}
                                                                    onClick={() => openEditModal(property.id)}
                                                                    className={css.clickableRow}
                                                                >
                                                                    <Cell>{property.name}</Cell>
                                                                    <Cell>{property.reforecastYear}</Cell>
                                                                    <Cell>{getMonthName(property.reforecastStartMonthIndex)}</Cell>
                                                                    <Cell>{property.isDeactivated ? 'Deactivated' : 'Active'}</Cell>
                                                                </Row>
                                                        ))
                                                )}
                                            </Body>
                                        </Table>
                                )}
                            </Col>
                        </GridRow>
                    </Grid>

                    <TemplatePropertyCreateModal
                        formVisible={createModalVisible}
                        onModalClose={closeCreateModal}
                        onCreate={refreshData}
                        budgetYear={props.budgetYear}
                    />

                    <TemplatePropertyEditModal
                        templatePropertyId={selectedTemplatePropertyId}
                        formVisible={editModalVisible}
                        budgetYear={props.budgetYear}
                        onModalClose={closeEditModal}
                        onUpdate={refreshData}
                    />
                </AdminWrapper>
            </ThemeProvider>
    );
}
